import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../Modal';
import DocMarkdown from './DocMarkdown';
import { DocIcon } from './DocIcons';
import { secrets as findSecrets } from '../../lib/docsMarkdown';
import { docsApi, errorOf, TYPES, TYPE_LABEL } from '../../lib/docs';
import { aiApi, aiError, useAiStatus } from '../../lib/ai';
import '../ai/Ai.css';

/**
 * Writing an article: Markdown with a toolbar, and the page it makes beside
 * it as you type.
 *
 * Markdown because it is what the rest of Jarvis writes (minutes, summaries)
 * and because the text IS the version - every save is a version, and a diff
 * of plain text is something a person can read. The toolbar is for anybody
 * who does not know the syntax; the preview is the same component the reader
 * sees, so what is previewed is what is published.
 *
 * Pictures by pasting or dropping them in, or the picture button; a file
 * (a PDF, a config) the same way, becoming a download chip. A link to
 * another article from a picker of the ones you may read. Ticket numbers
 * (JAR-2026-0107) and asset tags (IT-LAP-000001) need nothing - they become
 * chips by themselves.
 *
 * THE GUARD. Anything that looks like a password is flagged as you type,
 * and the server will not save it until you say it is not one - with the
 * reason to use the password manager instead.
 *
 * HELP ME WRITE (wave 3 batch 5), for the people GET /ai/status allows and
 * who may edit this article: Claude drafts a how-to from a resolved ticket
 * (its public messages only), or rewords the words selected. What it writes
 * lands HERE, unsaved, marked as Claude's; "Save & publish" waits until it
 * has been saved as a draft once - read before anybody else does. The same
 * editor is the Help editor, so Help gets it too.
 */

const CALLOUTS = [['note', 'Note'], ['tip', 'Tip'], ['warning', 'Warning'], ['danger', 'Danger']];

/** Help me write: a resolved ticket into a how-to, or the selected words improved. */
function HelpMeWrite({ id, selection, onDraft, onImproved, onClose }) {
  const [ticket, setTicket] = useState('');
  const [busy, setBusy] = useState(null);
  const [problem, setProblem] = useState(null);
  const run = async (mode) => {
    setBusy(mode); setProblem(null);
    try {
      const r = await aiApi.write(mode === 'ticket' ? { node_id: id, mode, ticket: ticket.trim() } : { node_id: id, mode, text: selection });
      if (mode === 'ticket') onDraft(r); else onImproved(r.text);
    } catch (err) {
      setProblem(aiError(err, 'Claude could not write that.'));
      setBusy(null);
    }
  };
  return (
    <Modal title="Help me write" subtitle="Claude drafts; you read it, change it and decide when it is published." onClose={onClose}>
      <div className="ai-write-choices" data-ai="write">
        <section className="ai-write-choice">
          <h3>Turn a fix into a how-to</h3>
          <p>The number of a resolved ticket. Only its public messages are used, and nobody is named.</p>
          <form className="ai-write-row" onSubmit={(e) => { e.preventDefault(); if (ticket.trim()) run('ticket'); }}>
            <input className="de-input" value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="e.g. JAR-2026-0107"
                   aria-label="Ticket number" maxLength={60} autoFocus />
            <button type="submit" className="btn btn-primary" disabled={Boolean(busy) || !ticket.trim()}>
              {busy === 'ticket' ? 'Writing…' : 'Draft the article'}
            </button>
          </form>
        </section>
        <section className="ai-write-choice">
          <h3>Improve the wording</h3>
          <p>{selection.trim() ? `Rewords the ${selection.length} characters you selected, keeping every fact and step.` : 'Select some words in the article first, then come back here.'}</p>
          <button type="button" className="btn btn-secondary" disabled={Boolean(busy) || !selection.trim()} onClick={() => run('improve')}>
            {busy === 'improve' ? 'Rewording…' : 'Improve the selection'}
          </button>
        </section>
        {problem && <p className="ai-problem" role="alert">{problem}</p>}
      </div>
    </Modal>
  );
}

function LinkPicker({ onPick, onClose }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      docsApi.get('/docs/link-targets', { q }).then((r) => { if (live) setList(r.articles); }).catch(() => { if (live) setList([]); });
    }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <Modal title="Link to an article" subtitle="Only articles you can read are listed." onClose={onClose}>
      <input className="de-input" type="search" placeholder="Search titles" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search articles" autoFocus />
      <ul className="de-pick-list">
        {(list || []).map((a) => (
          <li key={a.id}>
            <button type="button" className="de-pick" onClick={() => onPick(a)}>
              <DocIcon name={a.article_type} />
              <span><strong>{a.title}</strong><small>{[...(a.path || []), a.type_label].join(' › ')}</small></span>
            </button>
          </li>
        ))}
        {list && !list.length && <li className="de-none">No articles match.</li>}
        {!list && <li className="de-none" aria-busy="true">Searching…</li>}
      </ul>
    </Modal>
  );
}

export default function DocEditor({ id, article, onSaved, onClose }) {
  const [title, setTitle] = useState(article.title);
  const [type, setType] = useState(article.article_type);
  const [tags, setTags] = useState(article.tags || []);
  const [tagDraft, setTagDraft] = useState('');
  const [body, setBody] = useState(article.body || '');
  const [summary, setSummary] = useState('');
  const [base, setBase] = useState(article.version);
  const [mode, setMode] = useState(() => (typeof window !== 'undefined' && window.innerWidth >= 1100 ? 'split' : 'write'));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);       // { kind: 'ok'|'error'|'conflict', text }
  const [secretAsk, setSecretAsk] = useState(null);
  const [picker, setPicker] = useState(false);
  const [calloutMenu, setCalloutMenu] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  // Help me write: the dialog (with the selection it opened on), and whether
  // Claude's words are in the editor and not yet saved.
  const ai = useAiStatus();
  const [helpWrite, setHelpWrite] = useState(null);
  const [aiUnsaved, setAiUnsaved] = useState(false);
  const selRange = useRef(null);
  const ta = useRef(null);
  const fileInput = useRef(null);
  const saved = useRef({ title: article.title, type: article.article_type, tags: JSON.stringify(article.tags || []), body: article.body || '' });

  const dirty = title !== saved.current.title || type !== saved.current.type
    || JSON.stringify(tags) !== saved.current.tags || body !== saved.current.body;
  const live = useMemo(() => findSecrets(`${title}\n${body}`), [title, body]);

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /* --------------------------------------------------- editing the text */

  const edit = useCallback((fn) => {
    const el = ta.current;
    if (!el) return;
    const s = el.selectionStart; const e = el.selectionEnd;
    const { text, selStart, selEnd } = fn(body, s, e, body.slice(s, e));
    setBody(text);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(selStart, selEnd); });
  }, [body]);

  const wrap = (before, after = before, placeholder = 'text') => edit((t, s, e, sel) => {
    const inner = sel || placeholder;
    return { text: t.slice(0, s) + before + inner + after + t.slice(e), selStart: s + before.length, selEnd: s + before.length + inner.length };
  });

  const prefixLines = (make) => edit((t, s, e) => {
    const from = t.lastIndexOf('\n', s - 1) + 1;
    const toNl = t.indexOf('\n', e);
    const to = toNl < 0 ? t.length : toNl;
    const lines = t.slice(from, to).split('\n');
    const out = lines.map((l, i) => make(i) + l).join('\n');
    return { text: t.slice(0, from) + out + t.slice(to), selStart: from, selEnd: from + out.length };
  });

  const insertBlock = (block, selectFrom = null, selectLen = 0) => edit((t, s, e) => {
    const before = t.slice(0, s);
    const pad = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    const text = before + pad + block + '\n' + t.slice(e);
    const at = before.length + pad.length;
    return { text, selStart: at + (selectFrom ?? block.length), selEnd: at + (selectFrom ?? block.length) + selectLen };
  });

  const insertAt = (snippet) => edit((t, s, e) => ({ text: t.slice(0, s) + snippet + t.slice(e), selStart: s + snippet.length, selEnd: s + snippet.length }));

  const TOOLS = [
    { k: 'heading', label: 'Heading', icon: 'heading', run: () => prefixLines(() => '## ') },
    { k: 'bold', label: 'Bold (Ctrl+B)', icon: 'bold', run: () => wrap('**') },
    { k: 'italic', label: 'Italic (Ctrl+I)', icon: 'italic', run: () => wrap('*') },
    { k: 'code', label: 'Inline code', icon: 'code', run: () => wrap('`', '`', 'code') },
    { sep: true },
    { k: 'list', label: 'Bullet list', icon: 'list', run: () => prefixLines(() => '- ') },
    { k: 'olist', label: 'Numbered list', icon: 'olist', run: () => prefixLines((i) => `${i + 1}. `) },
    { k: 'task', label: 'Steps to tick off', icon: 'task', run: () => prefixLines((i) => `${i + 1}. [ ] `) },
    { sep: true },
    { k: 'codeblock', label: 'Code block (with a Copy button)', icon: 'copy', run: () => insertBlock('```powershell\n\n```', 14, 0) },
    { k: 'table', label: 'Table', icon: 'table', run: () => insertBlock('| Item | Value |\n| --- | --- |\n| | |', 2, 4) },
    { k: 'flow', label: 'Workflow steps (Step | Who | When)', icon: 'workflow', run: () => insertBlock('```flow\nFirst step | Who | When\nNext step | Who | When\n```', 8, 10) },
    { sep: true },
    { k: 'link', label: 'Web link (Ctrl+K)', icon: 'link', run: () => edit((t, s, e, sel) => {
      const text = sel || 'link text'; const url = 'https://';
      const snippet = `[${text}](${url})`;
      return { text: t.slice(0, s) + snippet + t.slice(e), selStart: s + text.length + 3, selEnd: s + text.length + 3 + url.length };
    }) },
    { k: 'doclink', label: 'Link to another article', icon: 'doc', run: () => setPicker(true) },
    { k: 'image', label: 'Picture or file', icon: 'image', run: () => fileInput.current?.click() }
  ];

  const onKey = (e) => {
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === 'b') { e.preventDefault(); wrap('**'); }
    else if (k === 'i') { e.preventDefault(); wrap('*'); }
    else if (k === 'k') { e.preventDefault(); TOOLS.find((t) => t.k === 'link').run(); }
    else if (k === 's') { e.preventDefault(); save(); }
  };

  /* --------------------------------------------------- files */

  const uploadFiles = async (files) => {
    const list = [...files].filter((f) => f && f.size);
    if (!list.length) return;
    setUploading((n) => n + list.length);
    for (const f of list) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await docsApi.upload(id, f);
        insertAt(`${/^!/.test(r.markdown) ? '\n' : ''}${r.markdown}${/^!/.test(r.markdown) ? '\n' : ' '}`);
      } catch (err) {
        setMsg({ kind: 'error', text: errorOf(err, `Could not add ${f.name || 'that file'}`) });
      }
      setUploading((n) => n - 1);
    }
  };
  const onPaste = (e) => {
    const files = [...(e.clipboardData?.files || [])];
    if (files.length) { e.preventDefault(); uploadFiles(files); }
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  /* --------------------------------------------------- tags */

  const addTag = (raw) => {
    const t = String(raw || '').trim().toLowerCase().replace(/\s+/g, '-').slice(0, 40);
    if (t && !tags.includes(t) && tags.length < 20) setTags([...tags, t]);
    setTagDraft('');
  };

  /* --------------------------------------------------- saving */

  const save = async ({ acknowledge = false, thenPublish = false } = {}) => {
    if (busy) return null;
    if (!title.trim()) { setMsg({ kind: 'error', text: 'Give it a title' }); return null; }
    setBusy(true); setMsg(null);
    try {
      let version = base;
      if (dirty || acknowledge) {
        const r = await docsApi.put(`/docs/articles/${id}/draft`, {
          title, article_type: type, tags, body, summary: summary || null, base_version: base, acknowledge_secrets: acknowledge
        });
        version = r.version;
        setBase(r.version);
        saved.current = { title, type, tags: JSON.stringify(tags), body };
        setSummary('');
        setAiUnsaved(false);
      }
      if (thenPublish) {
        await docsApi.post(`/docs/articles/${id}/publish`, {});
        setMsg({ kind: 'ok', text: `Published version ${version}. Readers see it now.` });
      } else {
        setMsg({ kind: 'ok', text: `Saved as version ${version} - a draft only editors see until it is published.` });
      }
      setSecretAsk(null);
      if (onSaved) onSaved({ version, published: thenPublish });
      setBusy(false);
      return version;
    } catch (e) {
      setBusy(false);
      if (e?.response?.status === 422 && e.response.data?.needs_acknowledgement) {
        setSecretAsk({ list: e.response.data.secrets, thenPublish });
        return null;
      }
      if (e?.response?.status === 409) {
        setMsg({ kind: 'conflict', text: errorOf(e) });
        return null;
      }
      setMsg({ kind: 'error', text: errorOf(e, 'Could not save the article') });
      return null;
    }
  };

  return (
    <div className="de">
      <div className="de-head">
        <input className="de-title" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" placeholder="Title" maxLength={200} />
        <div className="de-meta">
          <label className="de-field">
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)} className="de-select">
              {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </label>
          <div className="de-field de-tags">
            <span id="de-tags-label">Tags</span>
            <div className="de-tag-box" aria-labelledby="de-tags-label">
              {tags.map((t) => (
                <span key={t} className="de-tag">{t}
                  <button type="button" aria-label={`Remove tag ${t}`} onClick={() => setTags(tags.filter((x) => x !== t))}><DocIcon name="close" /></button>
                </span>
              ))}
              <input value={tagDraft} placeholder={tags.length ? '' : 'Add a tag'} aria-label="Add a tag"
                     onChange={(e) => setTagDraft(e.target.value)}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagDraft); }
                       if (e.key === 'Backspace' && !tagDraft && tags.length) setTags(tags.slice(0, -1));
                     }}
                     onBlur={() => tagDraft && addTag(tagDraft)} />
            </div>
          </div>
        </div>
      </div>

      <div className="de-bar" role="toolbar" aria-label="Formatting">
        {TOOLS.map((t, i) => (t.sep ? <span key={`s${i}`} className="de-sep" aria-hidden="true" /> : (
          <button key={t.k} type="button" className="de-tool" onClick={t.run} title={t.label} aria-label={t.label} data-tool={t.k}>
            <DocIcon name={t.icon} />
          </button>
        )))}
        <div className="de-callout-wrap">
          <button type="button" className="de-tool de-tool-word" aria-haspopup="menu" aria-expanded={calloutMenu}
                  onClick={() => setCalloutMenu((v) => !v)} data-tool="callout">
            <DocIcon name="alert" /><span>Callout</span>
          </button>
          {calloutMenu && (
            <div className="de-menu" role="menu">
              {CALLOUTS.map(([k, label]) => (
                <button key={k} type="button" role="menuitem" className={`de-menu-item dm-tone-${k}`}
                        onClick={() => { setCalloutMenu(false); insertBlock(`> [!${k.toUpperCase()}]\n> ${label} text`, k.length + 8, label.length + 5); }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        {ai?.can?.write && (
          <button type="button" className="de-tool de-tool-word" data-tool="ai-write"
                  title="Claude drafts a how-to from a resolved ticket, or improves the words you selected"
                  onClick={() => {
                    const el = ta.current;
                    const s = el ? el.selectionStart : 0;
                    const e = el ? el.selectionEnd : 0;
                    selRange.current = { s, e };
                    setHelpWrite({ selection: body.slice(s, e) });
                  }}>
            <span className="ai-badge">AI</span><span>Help me write</span>
          </button>
        )}
        <span className="de-grow" />
        <div className="de-modes" role="tablist" aria-label="View">
          {[['write', 'Write'], ['split', 'Side by side'], ['preview', 'Preview']].map(([m, label]) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m} className={`de-mode${mode === m ? ' is-on' : ''}${m === 'split' ? ' de-mode-split' : ''}`}
                    onClick={() => setMode(m)}>{label}</button>
          ))}
        </div>
        <input ref={fileInput} type="file" hidden multiple onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {live.length > 0 && (
        <div className="de-secret" role="alert">
          <DocIcon name="key" />
          <div>
            <strong>This looks like a password or secret</strong>
            <span>{live.map((s) => `line ${s.line}: ${s.label} (${s.excerpt})`).join(' · ')}</span>
            <span>Keep secrets in the firm’s password manager, and write here where to find them.</span>
          </div>
        </div>
      )}

      <div className={`de-panes de-${mode}`}>
        {mode !== 'preview' && (
          <div className={`de-write${dragOver ? ' is-drop' : ''}`}
               onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
               onDragLeave={() => setDragOver(false)} onDrop={onDrop}>
            <textarea ref={ta} className="de-text" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={onKey}
                      onPaste={onPaste} aria-label="Article text (Markdown)" spellCheck="true"
                      placeholder="Write here. Paste or drop pictures straight in." />
            {uploading > 0 && <span className="de-uploading" role="status">Adding {uploading} file{uploading === 1 ? '' : 's'}…</span>}
          </div>
        )}
        {mode !== 'write' && (
          <div className="de-preview" aria-label="Preview">
            <h1 className="da-title">{title || 'Untitled'}</h1>
            <DocMarkdown text={body} />
          </div>
        )}
      </div>

      {aiUnsaved && (
        <p className="de-msg ai-drafted" role="note" data-ai="unsaved">
          <span className="ai-badge">AI</span> Claude’s words are in the editor. Read them through and save a draft; publish once it is right.
        </p>
      )}

      {msg && <p className={`de-msg de-msg-${msg.kind}`} role={msg.kind === 'ok' ? 'status' : 'alert'}>
        {msg.text}
        {msg.kind === 'conflict' && <> <button type="button" className="btn btn-plain btn-small" onClick={() => onClose && onClose(true)}>Open the latest version</button></>}
      </p>}

      <div className="de-foot">
        <input className="de-input de-summary" value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={300}
               placeholder="What changed? (optional, shown in the history)" aria-label="What changed" />
        <div className="de-actions">
          <span className="de-state" aria-live="polite">{dirty ? 'Unsaved changes' : `Version ${base}`}</span>
          <button type="button" className="btn btn-outline" onClick={() => onClose && onClose(false)} disabled={busy}>Close</button>
          <button type="button" className="btn btn-secondary" onClick={() => save()} disabled={busy || !dirty}>Save draft</button>
          <button type="button" className="btn btn-primary" onClick={() => save({ thenPublish: true })} disabled={busy || aiUnsaved}
                  title={aiUnsaved ? 'Save Claude’s draft first and read it through' : undefined}>
            {busy ? 'Saving…' : 'Save & publish'}
          </button>
        </div>
      </div>

      {helpWrite && (
        <HelpMeWrite id={id} selection={helpWrite.selection} onClose={() => setHelpWrite(null)}
          onDraft={(r) => {
            setHelpWrite(null);
            // An empty article takes the draft whole, title too; otherwise it
            // goes in where the cursor was, nothing typed is replaced.
            if (!body.trim()) {
              setBody(r.body);
              if (!title.trim() || title === 'Untitled' || title === article.title) setTitle(r.title);
            } else {
              const at = selRange.current ? selRange.current.e : body.length;
              setBody([body.slice(0, at), `## ${r.title}`, r.body, body.slice(at)].join('\n\n').replace(/\n{3,}/g, '\n\n'));
            }
            setAiUnsaved(true);
            if (mode === 'preview') setMode('write');
          }}
          onImproved={(text) => {
            setHelpWrite(null);
            const { s, e } = selRange.current || { s: 0, e: 0 };
            setBody(body.slice(0, s) + text + body.slice(e));
            setAiUnsaved(true);
          }} />
      )}

      {picker && (
        <LinkPicker onClose={() => setPicker(false)} onPick={(a) => {
          setPicker(false);
          edit((t, s, e, sel) => {
            const snippet = `[${sel || a.title}](doc:${a.id})`;
            return { text: t.slice(0, s) + snippet + t.slice(e), selStart: s + snippet.length, selEnd: s + snippet.length };
          });
        }} />
      )}

      {secretAsk && (
        <Modal title="Is this a password?" subtitle="Jarvis will not save it until you decide." onClose={() => setSecretAsk(null)}
               footer={(
                 <>
                   <button type="button" className="btn btn-primary" onClick={() => setSecretAsk(null)}>Go back and remove it</button>
                   <button type="button" className="btn btn-danger" onClick={() => save({ acknowledge: true, thenPublish: secretAsk.thenPublish })}>It is not a secret - save anyway</button>
                 </>
               )}>
          <p>Documentation is read by people who should not hold the firm’s passwords - and every version is kept for ever, so a password saved once stays in the history.</p>
          <ul className="de-secret-list">
            {secretAsk.list.map((s, i) => <li key={i}><strong>Line {s.line}</strong>: {s.label} <code>{s.excerpt}</code></li>)}
          </ul>
          <p>Put it in the password manager and write <em>where</em> to find it instead, for example “Wi-Fi password: in the IT vault, item <em>Office Wi-Fi</em>”.</p>
        </Modal>
      )}
    </div>
  );
}
